import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    fetchPromotionNotificationMeta,
    searchPromotionNotificationUsers,
    sendPromotionNotification,
} from "../service/api/orders";

const TABS = {
    ALL: "all",
    SELECTED: "selected",
    NON_REGISTERED: "non_registered",
};

const emptyForm = {
    image: null,
    title: "",
    message: "",
    url: "",
    zone_id: "",
    user_ids: [],
};

const Notifications = () => {
    const fileInputRef = useRef(null);

    const [activeTab, setActiveTab] = useState(TABS.ALL);
    const [form, setForm] = useState(emptyForm);
    const [preview, setPreview] = useState(null);

    const [meta, setMeta] = useState({
        users_count: 0,
        subscriber_count: 0,
        app_users: 0,
        junk_alerts_count: 0,
        zones: [],
    });

    const [metaLoading, setMetaLoading] = useState(false);
    const [sending, setSending] = useState(false);

    const [userSearch, setUserSearch] = useState("");
    const [userLoading, setUserLoading] = useState(false);
    const [userResults, setUserResults] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);

    const pageTitle = useMemo(() => {
        if (activeTab === TABS.ALL) {
            return "SEND PUSH NOTIFICATION & ALERT TO ALL USERS";
        }

        if (activeTab === TABS.SELECTED) {
            return "SEND PUSH NOTIFICATION & ALERT TO SELECTED USERS";
        }

        return "SEND PUSH NOTIFICATION TO NON REGISTERED APP USERS";
    }, [activeTab]);

    useEffect(() => {
        loadMeta();
    }, []);

    useEffect(() => {
        if (activeTab !== TABS.SELECTED) return;

        const timer = setTimeout(() => {
            loadUsers(userSearch);
        }, 350);

        return () => clearTimeout(timer);
    }, [userSearch, activeTab]);

    const loadMeta = async () => {
        try {
            setMetaLoading(true);

            const response = await fetchPromotionNotificationMeta();

            if (response?.success) {
                setMeta(response.data);
            }
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Failed to load notification meta");
        } finally {
            setMetaLoading(false);
        }
    };

    const loadUsers = async (search = "") => {
        try {
            setUserLoading(true);

            const response = await searchPromotionNotificationUsers(search);

            if (response?.success) {
                setUserResults(response.data || []);
            }
        } catch (error) {
            console.error(error);
            setUserResults([]);
        } finally {
            setUserLoading(false);
        }
    };

    const handleChange = (key, value) => {
        setForm((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];

        if (!file) return;

        if (!file.type.startsWith("image/")) {
            alert("Please select a valid image");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            alert("Image should be less than 5MB");
            return;
        }

        handleChange("image", file);

        if (preview) {
            URL.revokeObjectURL(preview);
        }

        setPreview(URL.createObjectURL(file));
    };

    const removeImage = () => {
        handleChange("image", null);

        if (preview) {
            URL.revokeObjectURL(preview);
        }

        setPreview(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const addSelectedUser = (user) => {
        const exists = selectedUsers.some((item) => item.id === user.id);

        if (exists) return;

        const updatedUsers = [...selectedUsers, user];

        setSelectedUsers(updatedUsers);
        handleChange(
            "user_ids",
            updatedUsers.map((item) => item.id)
        );

        setUserSearch("");
        setUserResults([]);
    };

    const removeSelectedUser = (userId) => {
        const updatedUsers = selectedUsers.filter((user) => user.id !== userId);

        setSelectedUsers(updatedUsers);
        handleChange(
            "user_ids",
            updatedUsers.map((item) => item.id)
        );
    };

    const resetForm = () => {
        setForm(emptyForm);
        setSelectedUsers([]);
        setUserSearch("");
        setUserResults([]);

        if (preview) {
            URL.revokeObjectURL(preview);
        }

        setPreview(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const validateForm = () => {
        if (!form.title.trim()) {
            alert("Notification title is required");
            return false;
        }

        if (form.title.trim().length > 120) {
            alert("Notification title should be less than 120 characters");
            return false;
        }

        if (!form.message.trim()) {
            alert("Notification message is required");
            return false;
        }

        if (form.message.trim().length > 500) {
            alert("Notification message should be less than 500 characters");
            return false;
        }

        if (activeTab === TABS.SELECTED && form.user_ids.length === 0) {
            alert("Please select at least one user");
            return false;
        }

        return true;
    };

    const handleSend = async () => {
        if (!validateForm()) return;

        const confirmSend = window.confirm(
            "Are you sure you want to send this notification?"
        );

        if (!confirmSend) return;

        try {
            setSending(true);

            const formData = new FormData();

            formData.append("type", activeTab);
            formData.append("title", form.title.trim());
            formData.append("message", form.message.trim());
            formData.append("url", form.url?.trim() || "");

            if (form.image) {
                formData.append("image", form.image);
            }

            if (activeTab === TABS.ALL && form.zone_id) {
                formData.append("zone_id", form.zone_id);
            }

            if (activeTab === TABS.SELECTED) {
                form.user_ids.forEach((id) => {
                    formData.append("user_ids[]", id);
                });
            }

            const response = await sendPromotionNotification(formData);

            if (!response?.success) {
                throw new Error(response?.message || "Failed to send notification");
            }

            const result = response.data;

            alert(
                `Notification sent successfully.\n\nSent: ${result.sent_count}\nFailed: ${result.failed_count}\nAlerts Saved: ${result.alerts_saved_count}`
            );

            resetForm();
            loadMeta();
        } catch (error) {
            console.error(error);

            alert(
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong while sending notification"
            );
        } finally {
            setSending(false);
        }
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    return (
        <div className="notification-page">
            <div className="stats-row">
                <StatsCard
                    count={metaLoading ? "..." : meta.users_count}
                    label="REGISTERED CUSTOMERS"
                />
                <StatsCard
                    count={metaLoading ? "..." : meta.subscriber_count}
                    label="SUBSCRIBERS"
                />
                <StatsCard
                    count={metaLoading ? "..." : meta.app_users}
                    label="APP USERS"
                />
            </div>

            <div className="notification-card">
                <div className="tabs-column">
                    <TabButton
                        active={activeTab === TABS.ALL}
                        onClick={() => handleTabChange(TABS.ALL)}
                    >
                        To All
                    </TabButton>

                    <TabButton
                        active={activeTab === TABS.SELECTED}
                        onClick={() => handleTabChange(TABS.SELECTED)}
                    >
                        To Selected
                    </TabButton>

                    <TabButton
                        active={activeTab === TABS.NON_REGISTERED}
                        onClick={() => handleTabChange(TABS.NON_REGISTERED)}
                    >
                        To Non-Registered App Users
                    </TabButton>
                </div>

                <div className="form-area">
                    <h4 className="form-title">{pageTitle}</h4>

                    <div className="divider" />

                    <div className="form-row image-row">
                        <label>Notification Image:</label>

                        <div className="image-upload-wrapper">
                            <label className="image-upload-box">
                                {preview ? (
                                    <img src={preview} alt="Notification Preview" />
                                ) : (
                                    <div className="upload-placeholder">
                                        <div className="upload-icon">📄+</div>
                                        <p>Drop image here to upload</p>
                                    </div>
                                )}

                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    hidden
                                />
                            </label>

                            <div className="image-help-row">
                                <small>Recommended image size: 1600x1100, max 5MB</small>

                                {preview && (
                                    <button
                                        type="button"
                                        className="remove-image-btn"
                                        onClick={removeImage}
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {activeTab === TABS.SELECTED && (
                        <FormRow required label="Select Users">
                            <div className="user-select-wrapper">
                                <input
                                    type="text"
                                    placeholder="Search by name, phone or email"
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                    onFocus={() => {
                                        if (userResults.length === 0) {
                                            loadUsers(userSearch);
                                        }
                                    }}
                                />

                                {selectedUsers.length > 0 && (
                                    <div className="selected-users">
                                        {selectedUsers.map((user) => (
                                            <div key={user.id} className="selected-user-chip">
                                                <span>{user.text}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeSelectedUser(user.id)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {(userLoading || userResults.length > 0) && (
                                    <div className="user-results">
                                        {userLoading && (
                                            <div className="user-result-item muted">
                                                Searching...
                                            </div>
                                        )}

                                        {!userLoading &&
                                            userResults.map((user) => (
                                                <button
                                                    type="button"
                                                    key={user.id}
                                                    className="user-result-item"
                                                    onClick={() => addSelectedUser(user)}
                                                >
                                                    {user.text}
                                                </button>
                                            ))}
                                    </div>
                                )}
                            </div>
                        </FormRow>
                    )}

                    <FormRow required label="Notification Title">
                        <input
                            type="text"
                            placeholder="Notification Title"
                            value={form.title}
                            maxLength={120}
                            onChange={(e) => handleChange("title", e.target.value)}
                        />
                    </FormRow>

                    <FormRow required label="Message">
                        <textarea
                            placeholder="Notification Message"
                            value={form.message}
                            maxLength={500}
                            onChange={(e) => handleChange("message", e.target.value)}
                        />
                    </FormRow>

                    <FormRow label="URL">
                        <input
                            type="text"
                            placeholder="This link will be opened when notification is clicked"
                            value={form.url}
                            onChange={(e) => handleChange("url", e.target.value)}
                        />
                    </FormRow>

                    {activeTab === TABS.ALL && (
                        <FormRow label="Zone">
                            <select
                                value={form.zone_id}
                                onChange={(e) => handleChange("zone_id", e.target.value)}
                            >
                                <option value="">All Zones</option>

                                {meta.zones.map((zone) => (
                                    <option key={zone.id} value={zone.id}>
                                        {zone.name}
                                    </option>
                                ))}
                            </select>
                        </FormRow>
                    )}

                    <div className="send-row">
                        <button onClick={handleSend} disabled={sending}>
                            <span>✈</span>
                            {sending ? "SENDING..." : "SEND"}
                        </button>
                    </div>
                </div>
            </div>

            <style>{styles}</style>
        </div>
    );
};

const StatsCard = ({ count, label }) => {
    return (
        <div className="stats-card">
            <h3>{count}</h3>
            <p>{label}</p>
        </div>
    );
};

const TabButton = ({ active, children, onClick }) => {
    return (
        <button className={`tab-button ${active ? "active" : ""}`} onClick={onClick}>
            {children}
        </button>
    );
};

const FormRow = ({ label, required = false, children }) => {
    return (
        <div className="form-row">
            <label>
                {required && <span className="required">*</span>}
                {label}:
            </label>

            <div className="input-area">{children}</div>
        </div>
    );
};

const styles = `
.notification-page {
  padding: 28px 40px;
  background: #eef2f5;
  min-height: 100vh;
  font-family: Arial, sans-serif;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
  margin-bottom: 26px;
}

.stats-card {
  background: #ffffff;
  border-radius: 5px;
  padding: 22px 20px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.08);
}

.stats-card h3 {
  margin: 0;
  font-size: 22px;
  color: #111827;
}

.stats-card p {
  margin: 8px 0 0;
  font-size: 12px;
  color: #8b95a1;
}

.notification-card {
  background: #ffffff;
  border-radius: 5px;
  display: grid;
  grid-template-columns: 250px 1fr;
  padding: 20px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.08);
}

.tabs-column {
  padding-right: 24px;
}

.tab-button {
  width: 100%;
  display: block;
  border: none;
  background: transparent;
  text-align: left;
  padding: 14px 18px;
  font-size: 14px;
  color: #4b5563;
  cursor: pointer;
  margin-bottom: 4px;
}

.tab-button.active {
  background: #e7f0ff;
  color: #111827;
}

.form-area {
  padding-left: 20px;
}

.form-title {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  color: #111827;
}

.divider {
  height: 1px;
  background: #e5e7eb;
  margin: 14px 0 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 240px 1fr;
  align-items: center;
  margin-bottom: 18px;
}

.form-row label {
  font-size: 14px;
  color: #111827;
}

.required {
  color: red;
}

.input-area input,
.input-area select,
.input-area textarea {
  width: 100%;
  border: 1px solid #e1e5ea;
  border-radius: 4px;
  padding: 0 14px;
  outline: none;
  font-size: 14px;
  box-sizing: border-box;
}

.input-area input,
.input-area select {
  height: 38px;
}

.input-area textarea {
  min-height: 90px;
  padding-top: 10px;
  resize: vertical;
  font-family: Arial, sans-serif;
}

.input-area input:focus,
.input-area select:focus,
.input-area textarea:focus {
  border-color: #3b82f6;
}

.image-row {
  align-items: center;
}

.image-upload-wrapper small {
  display: block;
  color: #9ca3af;
  font-size: 12px;
}

.image-help-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
}

.remove-image-btn {
  border: none;
  background: transparent;
  color: #dc2626;
  font-size: 12px;
  cursor: pointer;
}

.image-upload-box {
  height: 260px;
  border: 1px dashed #e1e5ea;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #ffffff;
}

.image-upload-box img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.upload-placeholder {
  text-align: center;
  color: #6b7280;
}

.upload-icon {
  font-size: 44px;
  opacity: 0.4;
}

.upload-placeholder p {
  margin-top: 10px;
  font-size: 16px;
}

.user-select-wrapper {
  position: relative;
}

.selected-users {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}

.selected-user-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 13px;
}

.selected-user-chip button {
  border: none;
  background: transparent;
  font-size: 18px;
  cursor: pointer;
  color: #dc2626;
}

.user-results {
  margin-top: 6px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-radius: 4px;
  max-height: 240px;
  overflow-y: auto;
}

.user-result-item {
  width: 100%;
  display: block;
  text-align: left;
  padding: 10px 12px;
  border: none;
  background: #ffffff;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  font-size: 13px;
}

.user-result-item:hover {
  background: #f9fafb;
}

.user-result-item.muted {
  color: #9ca3af;
  cursor: default;
}

.send-row {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.send-row button {
  height: 36px;
  padding: 0 20px;
  background: #4285f4;
  color: #ffffff;
  border: none;
  border-radius: 3px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  gap: 8px;
  align-items: center;
}

.send-row button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

@media (max-width: 900px) {
  .notification-page {
    padding: 18px;
  }

  .stats-row {
    grid-template-columns: 1fr;
  }

  .notification-card {
    grid-template-columns: 1fr;
  }

  .tabs-column {
    padding-right: 0;
    margin-bottom: 20px;
  }

  .form-area {
    padding-left: 0;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
}
`;

export default Notifications;